'use client';

import React, { useState, useRef, Fragment } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import { PortableText } from 'next-sanity';

import { useTreatmentApiContext } from '../../../../modules/treatmentSelector/context/TreatmentApiContext';
import useTreatmentPageSelectorAnalytics from '../hooks/useTreatmentPageSelectorAnalytics';
import useStartConsultation, { setConsultationSessionData } from '../../../Consultation/hooks/useStartConsultation';
import { bloomreachPixelSendProductView } from '../../../../modules/analytics/bloomreach/BloomreachPixel/sendBloomreachPageViewTrackingEvent';
import { SanityBlockContent, sanityBlockContentToString } from '../../../../helpers/text/sanityBlockContentToString';
import dispatchGoogleAnalyticsEvent, { gaEvents, gaFormatters } from '../../../../modules/analytics/google/dispatchGoogleAnalyticsEvent';
import { titleHighlightedComponents } from '../../../_ui/_blocks/HighlightedTitle/HighlightedTitle';
import formatPrice from '../../../../helpers/formatPrice';

import TreatmentSelector, { SelectorOnChangeData, SelectorOnSubmitData } from '../../../../modules/treatmentSelector/components/TreatmentSelector';
import LoadingSkeleton from '../../../../modules/treatmentSelector/components/LoadingSkeleton/LoadingSkeleton';
import ImageWithFallback from '../../../NextJs/ImageWithFallback/ImageWithFallback';
import Typography from '../../../_ui/_blocks/Typography/Typography';
import ReviewsLink from './ReviewsLink/ReviewsLink';
import ReasonsToBuy from './ReasonsToBuy/ReasonsToBuy';
import TreatmentPageButton from './TreatmentPageButton/TreatmentPageButton';

import styles from './NewSelector.module.css';

const TreatmentSelectorComponent = dynamic(() => import('../../../../modules/treatmentSelector/components/TreatmentSelector'), {
    ssr: false,
    loading: LoadingSkeleton,
});

type ReasonsToBuyProps = React.ComponentProps<typeof ReasonsToBuy>;
type ReviewsLinkProps = React.ComponentProps<typeof ReviewsLink>;

interface Props extends ReviewsLinkProps {
    initialImageSrc: string;
    title: string | SanityBlockContent;
    fromPrice: number;
    conditionName: string;
    conditionSlug: string;
    isPrivatePrescription?: boolean;
    gppcFromPrice?: string;
    reasons: ReasonsToBuyProps['reasons'] | null;
}

const NewSelector = ({
    initialImageSrc,
    title,
    review,
    fromPrice,
    gppcFromPrice,
    conditionName,
    conditionSlug,
    isPrivatePrescription,
    reasons,
}: Props) => {
    // TODO: Have put this for bloomreach tracking. in next js we can probably move this
    const trackingHasRun = useRef(false);

    const { isLoading, error, treatment } = useTreatmentApiContext();
    const startConsultation = useStartConsultation();

    const router = useRouter();

    useTreatmentPageSelectorAnalytics(treatment);

    const [details, setDetails] = useState({
        imgSrc: initialImageSrc,
    });

    const handleSubmit = ({ variant, quantity, isRefill }: SelectorOnSubmitData) => {
        dispatchGoogleAnalyticsEvent(
            gaEvents.GrowthBookEvent,
            gaFormatters.growthbookEvent('treatment-selector', 'selection_complete'),
        );

        if (isRefill || ['PRODUCT', 'PMED'].includes(variant.type)) {
            startConsultation(conditionSlug, {
                conditionId: treatment!.condition_id,
                treatmentId: variant.id,
                quantity: quantity.quantity,
                selectedVariant: variant,
                selectedQuantityInfo: quantity,
                selectedTreatment: treatment!,
            }, isRefill);
            return;
        }

        const quantityForUrl = quantity?.quantity || 0;

        // !NOTE!: treatmentId has been removed because if we send it, the treatment recommendation panel
        // wont be displayed.
        setConsultationSessionData({
            conditionId: treatment!.condition_id,
            selectedVariant: variant,
            selectedQuantityInfo: quantity || undefined,
            selectedTreatment: treatment!,
        });

        router.push(`/${conditionSlug}/request-consultation?variant=${variant.id}&quantity=${quantityForUrl}`);
    };

    const handleOnChange = ({ variant }: SelectorOnChangeData) => {
        let imgSrc = initialImageSrc;

        if (variant.public && Array.isArray(variant.public) === false) {
            imgSrc = variant.public.images.mediumFeaturedUrl || imgSrc;
        }

        // Grab the treatment parent id other wise use the current treatment id, other wise use the variant id.
        const prodId = (treatment?.parent && treatment?.parent?.reference ? treatment.parent.reference : treatment?.id) || variant.id;

        if (trackingHasRun.current === false) {
            bloomreachPixelSendProductView({
                prod_id: prodId.toString(),
                prod_name: variant.name,
                sku: variant.id.toString(), // Product ID - (@Phil mentioned we should just use the id as the sku)
            });
            trackingHasRun.current = true;
        }

        setDetails({
            imgSrc,
        });
    };

    return (
        <div className={styles.grid}>
            <div className={styles.image}>
                <ImageWithFallback
                    fallback="/images/logo-dark.svg"
                    src={details.imgSrc}
                    alt={sanityBlockContentToString(title)}
                    priority
                    width={280}
                    height={280}
                />
            </div>
            <div className={styles.header}>
                <Typography
                    as="h1"
                    typeset="title"
                    size="500"
                    lineHeight="500"
                    className="md:type-800 md:leading-800"
                >
                    {typeof title === 'string'
                        ? title
                        : <PortableText components={titleHighlightedComponents()} value={title} />}
                </Typography>
                <div className="flex gap-050 justify-between mt-050">
                    <ReviewsLink review={review} />
                    <Typography
                        as="div"
                        typeset="emphasis"
                        lineHeight="none"
                        className="whitespace-nowrap"
                        color="price"
                    >
                        {gppcFromPrice || (
                            <Fragment>
                                <Typography size="090" color="foundation">from</Typography>{' '}
                                {formatPrice(fromPrice)}
                            </Fragment>
                        )}
                    </Typography>
                </div>
            </div>
            {reasons?.length ? <ReasonsToBuy reasons={reasons} className={styles.reasonsToBuy} /> : null}
            {/* Wrapped in div to avoid elements inside growing to the grid height. */}
            <div className={styles.treatmentSelector}>
                <TreatmentSelectorComponent
                    onSubmit={handleSubmit}
                    onChange={handleOnChange}
                    treatment={treatment}
                    isError={!!error}
                    isLoading={isLoading}
                    className="space-y-150"
                    conditionSlug={conditionSlug}
                >
                    <TreatmentSelector.PricingFrame>
                        <TreatmentSelector.VariantSelector />
                        <TreatmentSelector.QuantitySelector />
                        <TreatmentSelector.TotalsSummary />
                    </TreatmentSelector.PricingFrame>
                    {isPrivatePrescription ? (
                        <TreatmentSelector.PrivatePrescriptionNotification />
                    ) : null}
                    <TreatmentSelector.Notifications conditionSlug={conditionSlug} />
                    <TreatmentSelector.ConfirmSelectionFrame conditionName={conditionName}>
                        <TreatmentPageButton
                            conditionSlug={conditionSlug}
                            className="w-full text-center"
                            loading={false}
                        />
                    </TreatmentSelector.ConfirmSelectionFrame>
                </TreatmentSelectorComponent>
            </div>
        </div>
    );
};

export default NewSelector;
